import { useEffect, useMemo, useState } from "react";
import {
  Dropdown,
  makeStyles,
  Option,
  Skeleton,
  Spinner,
  useId,
} from "@fluentui/react-components";

type OptionType = {
  label: string;
  value: string;
};

type ComponentProps = {
  options: OptionType[];
  selectedDropDownValues: string[];
  setSelectedDropDownValues: (values: string[]) => void;

  // 🔥 NEW: expose selected labels
  setSelectedLabels?: (labels: string[]) => void;

  placeholder?: string;
  onOpenChange?: (e: any, data: { open: boolean }) => void;
  style?: any;
  select_all?: boolean;
  fixed_width?: boolean;
  maxSelectionLimit?: number;
  allSelectedText?: string;
  multiselect?: boolean;
  loading?: boolean;
};

const useStyles = makeStyles({
  root: {
    maxWidth: "100% !important",
    maxHeight: "35px",
    overflowX: "auto",
  },
  fixed_root: {},
  fixed_dropdown_button: {
    minWidth: "150px !important",
    maxWidth: "350px !important",
    maxHeight: "35px !important",
    overflowX: "auto",
  },
  dropdown_button: {
    width: "100% !important",
  },
  listbox: {
    maxHeight: "50vh !important",
    overflowY: "auto",
  },
});

const Multiselectsearch = (props: ComponentProps) => {
  const {
    options,
    selectedDropDownValues,
    setSelectedDropDownValues,
    setSelectedLabels,
    placeholder,
    onOpenChange,
    select_all = true,
    fixed_width = true,
    maxSelectionLimit,
    multiselect = true,
    allSelectedText,
    loading
  } = props;

  const comboId = useId("combo-multi");
  const styles = useStyles();

  const [optionState, setOptionState] = useState<OptionType[]>([]);

  /* ================= NORMALIZE OPTIONS ================= */
  useEffect(() => {
    const uniqueOptions = options.filter(
      (option, index, self) =>
        index === self.findIndex((t) => t.value === option.value),
    );
    setOptionState(uniqueOptions);
  }, [options]);

  const uniqueOptionValues = useMemo(
    () => optionState.map((o) => o.value),
    [optionState],
  );

  const normalizedSelected = useMemo(
    () =>
      Array.from(
        new Set(
          selectedDropDownValues.filter((v) =>
            uniqueOptionValues.includes(v),
          ),
        ),
      ),
    [selectedDropDownValues, uniqueOptionValues],
  );

  const isAllSelected =
    uniqueOptionValues.length > 0 &&
    normalizedSelected.length === uniqueOptionValues.length;

  /* ================= EXPOSE LABELS TO PARENT ================= */
  useEffect(() => {
    if (!setSelectedLabels) return;

    const labels = normalizedSelected
      .map((value) => {
        const match = optionState.find((o) => o.value === value);
        return match?.label;
      })
      .filter(Boolean) as string[];

    setSelectedLabels(labels);
  }, [normalizedSelected, optionState, setSelectedLabels]);

  /* ================= HANDLE SELECT ================= */
  const handleOptionChange = (_: any, option: any) => {
    if (!option) return;

    const val = option.optionValue;

    if (multiselect) {
      let newSelectedValues = [...normalizedSelected];

      if (val === "select-all") {
        if (isAllSelected) {
          newSelectedValues = [];
        } else {
          newSelectedValues =
            maxSelectionLimit &&
              uniqueOptionValues.length > maxSelectionLimit
              ? uniqueOptionValues.slice(0, maxSelectionLimit)
              : [...uniqueOptionValues];
        }
      } else {
        const alreadySelected = newSelectedValues.includes(val);

        if (alreadySelected) {
          newSelectedValues = newSelectedValues.filter((v) => v !== val);
        } else if (
          !maxSelectionLimit ||
          newSelectedValues.length < maxSelectionLimit
        ) {
          newSelectedValues.push(val);
        }
      }

      newSelectedValues = Array.from(new Set(newSelectedValues)).filter((v) =>
        uniqueOptionValues.includes(v),
      );

      setSelectedDropDownValues(newSelectedValues);
    } else {
      if (uniqueOptionValues.includes(val)) {
        setSelectedDropDownValues([val]);
      }
    }
  };

  /* ================= DISPLAY TEXT ================= */
  const displayText = useMemo(() => {
    if (normalizedSelected.length === 0) return undefined;

    if (isAllSelected) {
      return allSelectedText || "All";
    }

    const firstTwo = normalizedSelected.slice(0, 2);
    const remaining = normalizedSelected.length - 2;

    const selectedLabels = firstTwo
      .map((value) => {
        const match = optionState.find((o) => o.value === value);
        return match?.label || "";
      })
      .filter(Boolean);

    return remaining > 0
      ? `${selectedLabels.join(", ")} +${remaining}`
      : selectedLabels.join(", ");
  }, [normalizedSelected, optionState, isAllSelected, allSelectedText]);

  /* ================= RENDER ================= */
  return (
    <div className={fixed_width ? styles.root : styles.fixed_root}>
      <Dropdown
        listbox={{ className: styles.listbox }}
        className={
          fixed_width ? styles.fixed_dropdown_button : styles.dropdown_button
        }
        aria-labelledby={comboId}
        multiselect={multiselect}
        placeholder={placeholder}
        selectedOptions={
          multiselect ? normalizedSelected : normalizedSelected.slice(0, 1)
        }
        value={displayText}
        onOptionSelect={handleOptionChange}
        onOpenChange={(e, data) => {
          if (onOpenChange) onOpenChange(e, data);
        }}
      >
        {/* 🔥 SHOW SKELETON WHEN LOADING */}
        {loading ? (
          <Option
            value="loading"
            text="Loading..."
            disabled
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 0",
              }}
            >
              <Spinner size="tiny" />
              <span>Loading...</span>
            </div>
          </Option>
        ) : (
          <>
            {/* ✅ Select All only after options exist */}
            {select_all && optionState.length > 0 && (
              <Option value="select-all" key="option-select-all">
                Select All
              </Option>
            )}

            {optionState.map((option, index) => (
              <Option
                value={option.value}
                key={`option-${index}`}
                text={option.label}
                disabled={
                  maxSelectionLimit &&
                    !normalizedSelected.includes(option.value) &&
                    normalizedSelected.length >= maxSelectionLimit ? true : false
                }
              >
                {option.label}
              </Option>
            ))}
          </>
        )}
      </Dropdown>
    </div>
  );
};

export default Multiselectsearch;